import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get_all_orders } from "../../reduxData/user/userAction";
import CustomPagination from "../../components/CustomPagination";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime } from "../../hooks/formatDateTime";
import { formatPrice } from "../../hooks/formatPrice";
import EmptyData from "../../components/EmptyData";
import { useTranslation } from "react-i18next";
import TranslatedText from "../../hooks/TranslatedText";

const MyOrders = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [expandedRows, setExpandedRows] = useState({});
    const { user, userDetails, userPermissions } = useSelector((state) => state.auth);
    const { orderLists, totalOrders } = useSelector((state) => state.user);
    const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;
    const [statusFilter, setStatusFilter] = useState("all");

    // manage width function 
    const [width, setWidth] = useState(window.innerWidth);
    const isMobile = width <= 750;
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // feth order data 
    const fetchData = async (filter) => {
        await get_all_orders(dispatch, 1, 10, filter);
    };

    // call order data function 
    useEffect(() => {
        fetchData('all');
    }, []);

    // handle filter change 
    const handleFilterChange = (newFilter) => {
        setStatusFilter(newFilter);
        fetchData(newFilter);
    };

    // image url function 
    const getFullImageUrl = (url) => {
        const isExternal = url?.startsWith("http") || url?.startsWith("https");
        return isExternal ? url : url ? `${REACT_APP_IMAGE_URL}${url}` : null;
    };

    // navigate product detail function 
    const handleDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    // navigate order detail function 
    const handleOrderDetail = (item) => {
        navigate(`/order/${item?.order_id || item?.orderId}`, {
            state: { orderDetail: item }
        });
    };

    // handle order toggle function 
    const toggleRow = (orderId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [orderId]: !prev[orderId],
        }));
    };

    // order status classes 
    const getOrderStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === "placed" || s === "pending" || s === "order_received") return "bg-primary";
        if (s === "cancelled") return "bg-secondary";
        return "bg-primary";
    };

    // order payment status classes
    const getPaymentStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === "succeeded") return "bg-success";
        if (s === "success") return "bg-success";
        if (s === "pending") return "bg-warning text-dark";
        if (s === "failed") return "bg-danger";
        return "bg-secondary";
    };

    // risk status classes 
    const getRiskStatus = (item) => {
        if (item?.is_dispute) {
            return {
                label: t("order.DisputeRisk"),
                className: "bg-danger",
            };
        }
        if (item?.is_return) {
            return {
                label: t("order.ReturnRisk"),
                className: "bg-danger",
            };
        }
        if (item?.paymentdata?.status === "failed") {
            return {
                label: t("order.PaymentFailedRisk"),
                className: "bg-danger",
            };
        }
        if (item?.paymentdata?.seller_payment_status === "blocked") {
            return {
                label: t("order.PayoutBlockedRisk"),
                className: "bg-danger",
            };
        }
        return {
            label: t("order.None"),
            className: "bg-success",
        };
    };

    // get ordered variants 
    const getPurchasedVariants = (order) => {
        const orderedIds = order?.ordered_variant_id || [];
        if (!orderedIds.length) return [];

        return orderedIds.map((variantId) => {
            const parentItem = order.itemsdata?.find(item =>
                item.variantsData?.some(v => v.id === variantId)
            );

            if (!parentItem) return null;

            const variant = parentItem.variantsData?.find(v => v.id === variantId);

            return {
                parentItem,
                variant,
                variantId,
            };
        }).filter(Boolean);
    };

    // desktop view 
    const DesktopTable = () => (
        <div className="si-table-wrapper">
            <table className="si-table">
                <colgroup>
                    <col className="si-col-toggle" />
                    <col className="si-col-order" />
                    <col className="si-col-items" />
                    <col className="si-col-amount" />
                    <col className="si-col-payment" />
                    <col className="si-col-payment" />
                    <col className="si-col-status" />
                    <col className="si-col-status" />
                    <col className="si-col-date" />
                    <col className="si-col-action" />
                </colgroup>

                <thead className="si-thead">
                    <tr>
                        <th className="si-th si-th--center" />
                        <th className="si-th">{t("order.OrderId")}</th>
                        <th className="si-th si-th--center">{t("order.NoOfItems")}</th>
                        <th className="si-th si-th--right">{t("order.TotalAmount")}</th>
                        <th className="si-th si-th--center">{t("order.OrderStatus")}</th>
                        <th className="si-th si-th--center">{t("payments.buyerStatus")}</th>
                        <th className="si-th si-th--center">{t("order.Risk")}</th>
                        <th className="si-th si-th--center">{t("payments.sellerStatus")}</th>
                        <th className="si-th si-th--center">{t("order.OrderDate")}</th>
                        <th className="si-th si-th--center">{t("order.Action")}</th>
                    </tr>
                </thead>

                <tbody>
                    {orderLists?.map((item, index) => {
                        const orderId = item?.order_id || item?.orderId;
                        const isExpanded = !!expandedRows[orderId];
                        const purchasedVariants = getPurchasedVariants(item);
                        // const itemCount = purchasedVariants.length || 1;
                        const itemCount = item?.orders_quantity || 1;
                        const hasMultiple = itemCount > 0;
                        const risk = getRiskStatus(item);

                        return (
                            <React.Fragment key={orderId || index}>
                                <tr className={`si-row si-row--parent ${isExpanded ? "si-row--expanded" : ""}`}>
                                    <td className="si-td si-td--center">
                                        {hasMultiple ? (
                                            <button
                                                className={`si-toggle-btn ${isExpanded ? "si-toggle-btn--open" : ""}`}
                                                onClick={() => toggleRow(orderId)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        ) : null}
                                    </td>

                                    <td className="si-td">
                                        <span className="si-order-id">{orderId}</span>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button
                                            className={`si-count-badge ${hasMultiple ? "si-count-badge--clickable" : ""}`}
                                            onClick={() => hasMultiple && toggleRow(orderId)}
                                            disabled={!hasMultiple}
                                        >
                                            {itemCount} {itemCount === 1 ? t("order.Item") : t("order.Items")}
                                        </button>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <span className="si-amount">
                                            {formatPrice(item?.total_amount ?? item?.amount, i18n)}
                                        </span>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <StatusBadge status={item?.order_status} getClass={getOrderStatusClass} />
                                    </td>

                                    <td className="si-td si-td--center">
                                        <StatusBadge status={item?.paymentsdata?.status} getClass={getPaymentStatusClass} />
                                    </td>
                                    <td className="si-td si-td--center">
                                        <span className={`badge px-3 py-2 bg-secondary ${risk.className}`}>{risk.label}</span>
                                    </td>
                                    <td className="si-td si-td--center">
                                        <StatusBadge status={item?.paymentsdata?.seller_payment_status} getClass={getPaymentStatusClass} />
                                    </td>

                                    <td className="si-td si-td--center si-date">
                                        {formatDateTime(item?.order_created_at, i18n)}
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button className="si-view-btn" onClick={() => handleOrderDetail(item)}>
                                            {t("order.View")}
                                        </button>
                                    </td>
                                </tr>

                                {isExpanded && purchasedVariants.map(({ parentItem, variant }, subIndex) => {
                                    const itemImage = variant?.images?.[0] || parentItem?.image_urls?.[0] || null;
                                    const itemTitle = parentItem?.title || "—";
                                    const itemPrice = variant?.bid_amount || parentItem?.original_retail_price || parentItem?.amount;
                                    const qty = parentItem?.quantity;

                                    return (
                                        <tr key={`${orderId}-sub-${subIndex}`} className="si-row si-row--child">
                                            <td className="si-td si-td--indent" />
                                            <td className="si-td si-sub-full" colSpan={10}>
                                                <div className="si-sub-product">
                                                    <img
                                                        src={getFullImageUrl(itemImage)}
                                                        alt={itemTitle}
                                                        className="si-sub-img"
                                                    />
                                                    <div className="si-sub-info">
                                                        <span className="si-sub-title" onClick={() => handleDetail(parentItem?.slug)}>
                                                            {itemTitle}
                                                        </span>
                                                        {(parentItem?.is_return || parentItem?.is_dispute) &&
                                                            <div className="si-sub-chips">
                                                                {parentItem?.is_dispute && <span className="od2-attr-chip">{t("order.Disputed")}</span>}
                                                                {parentItem?.is_return && <span className="od2-attr-chip">{t("order.Returned")}</span>}
                                                            </div>
                                                        }
                                                    </div>
                                                    <div className="si-sub-chips">
                                                        {variant?.size_name && (
                                                            <span className="od2-attr-chip">
                                                                {t("order.Size")}: <strong>{variant.size_name}</strong>
                                                            </span>
                                                        )}
                                                        {(variant?.color_name || variant?.color_code) && (
                                                            <span className="od2-attr-chip">
                                                                {t("order.Color")}: <strong>
                                                                    <TranslatedText text={variant?.color_name} />
                                                                </strong>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="si-sub-price-inline">
                                                        {formatPrice(itemPrice, i18n)}
                                                    </span>
                                                    {(qty) && (
                                                        <span className="od2-attr-chip">
                                                            {t("order.Quantity")}: <strong>{qty}</strong>
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div >
    );

    // mobile view 
    const MobileCards = () => (
        <div className="si-mobile-list">
            {orderLists?.map((item, index) => {
                const orderId = item?.order_id || item?.orderId;
                const isExpanded = !!expandedRows[orderId];
                const purchasedVariants = getPurchasedVariants(item);
                const itemCount = purchasedVariants.length || 1;
                const hasMultiple = itemCount > 0;

                return (
                    <div key={orderId || index} className="si-card">
                        <div className="si-card-header">
                            <div className="si-card-header-left">
                                <span className="si-card-order-id">#{orderId}</span>
                                <span className="si-card-date">
                                    {formatDateTime(item?.order_created_at, i18n)}
                                </span>
                            </div>
                            <span className="si-card-amount">
                                {formatPrice(item?.total_amount ?? item?.amount, i18n)}
                            </span>
                        </div>

                        <div className="si-card-badges">
                            <div className="si-card-badge-group">
                                <span className="si-card-badge-label">{t("order.PaymentStatus")}</span>
                                <StatusBadge status={item?.paymentsdata?.status} getClass={getPaymentStatusClass} />
                            </div>
                            <div className="si-card-badge-group">
                                <span className="si-card-badge-label">{t("order.OrderStatus")}</span>
                                <StatusBadge status={item?.paymentsdata?.seller_payment_status} getClass={getPaymentStatusClass} />
                            </div>
                        </div>

                        {hasMultiple && (
                            <button
                                className="si-card-expand-btn"
                                onClick={() => toggleRow(orderId)}
                            >
                                <svg
                                    width="14" height="14" viewBox="0 0 14 14" fill="none"
                                    className={isExpanded ? "si-icon--flipped" : ""}
                                >
                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {isExpanded ? t("order.HideItems") : `${t("order.ShowItems")} ${itemCount} ${t("order.Items")}`}
                            </button>
                        )}

                        {isExpanded && (
                            <div className="si-card-items">
                                {purchasedVariants.map(({ parentItem, variant }, idx) => {
                                    const itemImage = variant?.images?.[0] || parentItem?.image_urls?.[0] || null;
                                    const itemTitle = parentItem?.title || "—";
                                    const itemPrice = variant?.bid_amount || parentItem?.original_retail_price || parentItem?.amount;

                                    return (
                                        <div key={idx} className="si-card-subitem">
                                            <img
                                                src={getFullImageUrl(itemImage)}
                                                alt={itemTitle}
                                                className="si-card-subitem-img"
                                            />
                                            <div className="si-card-subitem-info">
                                                <span className="si-card-subitem-title" onClick={() => handleDetail(parentItem?.slug)}>
                                                    {itemTitle}
                                                </span>
                                                <div className="si-card-subitem-chips">
                                                    {variant?.size_name && (
                                                        <span className="od2-attr-chip">
                                                            {t("order.Size")}: <strong>{variant.size_name}</strong>
                                                        </span>
                                                    )}
                                                    {(variant?.color_name || variant?.color_code) && (
                                                        <span className="od2-attr-chip">
                                                            {t("order.Color")}: <strong><TranslatedText text={variant?.color_name} />
                                                            </strong>
                                                        </span>
                                                    )}
                                                    <span className="si-card-subitem-price">
                                                        {formatPrice(itemPrice, i18n)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <div className="si-card-footer">
                            <button className="si-view-btn" onClick={() => handleOrderDetail(item)}>
                                {t("order.View")}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="si-page container">
            {totalOrders > 0 ? (
                isMobile ? <MobileCards /> : <DesktopTable />
            ) : (
                <EmptyData />
            )}

            {totalOrders > 0 && (
                <div className="mt-4">
                    <CustomPagination
                        total={totalOrders}
                        onPageChange={(page, perPage) => {
                            get_all_orders(dispatch, page, perPage, statusFilter);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default MyOrders;
